import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";
import moment from "moment";

// Utils
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";

// Serializer
import BillingBLInterfaceSerializer from "issara-sdk/types/BillingBLInterfaceSerializer_apps_INF";

// API
import BillingBLInterfaceList from "issara-sdk/apis/BillingBLInterfaceList_apps_INF";
import BillingBLInterfaceUpdateView from "issara-sdk/apis/BillingBLInterfaceUpdateView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";

export type Props = {

}

export type State = Partial<{
  // seq
  BillingBLInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    billingBLInterfaceList: BillingBLInterfaceSerializer[];
    selectedItems: any[];
    userOptions: any[];
    searchBillingBL: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type FilterSearchType = Partial<{
  createdDate: string | null;
  hn: string | null;
  invoiceItemId: string | null;
  documentNo: string | null;
  documentType: string | null;
}>;

type PaginatedItemsType = Partial<{
  items: BillingBLInterfaceSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "BillingBLInterface";
  restart?: boolean;
  clear?: boolean;
};

export type SaveType = "ADD" | "EDIT";

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof BillingBLInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof BillingBLInterfaceSerializer, number>>;

// Handle Action
type ActionType =
  | { action: "SEARCH"; cardKey: string; page?: number; pageSize?: number }
  | { action: "CLEAR"; cardKey: string }
  | { action: "SAVE"; cardKey: string; type: SaveType; selectedItems: any[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  BillingBLInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  errorMessage: null,
  successMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardBillingBLInterface";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  hn: "",
  invoiceItemId: "",
  documentNo: "",
  documentType: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.setState(
    {
      BillingBLInterfaceSequence: {
        ...state.BillingBLInterfaceSequence,
        sequenceIndex: "Action",
        searchBillingBL: {
          createdDate: currentDate,
        },
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    // () => Action(controller, { ...params, action: "SEARCH", cardKey: CARD_NAME })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchBillingBL = state.BillingBLInterfaceSequence?.searchBillingBL;
  const paginatedItems = state.BillingBLInterfaceSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  let data = {
    limit: pageSize,
    offset,
    create_date: searchBillingBL?.createdDate,
    hn: searchBillingBL?.hn,
    invoice_item_id: searchBillingBL?.invoiceItemId,
    document_no: searchBillingBL?.documentNo,
    document_type: searchBillingBL?.documentType,
  };

  const result = await GetBillingBLInterfaceData(controller, data);

  if (result?.items) {
    const uniqueData = (result.items || [])
      .filter(
        (item: BillingBLInterfaceSerializer, index: number, self: any) =>
          index === self.findIndex((t: any) => t.created_by === item.created_by)
      )
      .map((item: BillingBLInterfaceSerializer) => item?.created_by);

    const userOptions = await Promise.all(
      uniqueData.map(async (id: number) => {
        const user = await GetUserList(controller, { id });

        return user;
      })
    );

    controller.setState({
      BillingBLInterfaceSequence: {
        ...state.BillingBLInterfaceSequence,
        billingBLInterfaceList: result?.items,
        userOptions: userOptions,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${CARD_NAME}_${params.type}`]: "LOADING",
    },
  });

  const [saveResp, saveErr] = await SaveBillingBLInterface(controller, {
    type: params.type,
    selectedItems: params.selectedItems,
  });

  if (saveResp) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "The system has successfully",
      },
    });

    HandleSearchData(controller, { action: "SEARCH", cardKey: CARD_NAME });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.setState({
    BillingBLInterfaceSequence: {
      ...state.BillingBLInterfaceSequence,
      searchBillingBL: {
        ...FILTER_INIT,
        createdDate: currentDate,
      },
      billingBLInterfaceList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    }
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetBillingBLInterfaceData: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await BillingBLInterfaceList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetUserList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await UserList.list({
    apiToken: controller.apiToken,
    params: { pk: params.id },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items?.[0];
};

const SaveBillingBLInterface: Handler = async (controller, params) => {
  const state = controller.getState();

  const apiType =
    params.type === "ADD"
      ? BillingBLInterfaceUpdateView.post
      : BillingBLInterfaceUpdateView.put;

  const [resp, err] = await apiType({
    apiToken: controller.apiToken,
    data: {
      item_id_list: params?.selectedItems || [],
    },
  });

  if (err) return [null, err];

  return [resp, err];
};

/**========================================================================
 **                               Utils
 *========================================================================**/

export const accessorList: Partial<Record<keyof BillingBLInterfaceSerializer, string>> = {
  id: "ID",
  valid: "Valid",
  error_message: "Error Message",
  created_by: "User ที่สร้าง",
  created_at: "วันเวลาที่สร้าง",
  sent: "Sent",
  file_name: "File Name",
  version: "Version",
  record_type: "Record Type",
  company_code: "Company Code",
  document_no: "Document No",
  status: "Status",
  document_type: "Document Type",
  hn: "HN",
  document_date: "Document Date",
  encounter_type: "Encounter Type",
  encounter_id: "Encounter ID",
  obs_type: "OBS Type",
  obs_id: "OBS ID",
  obs_seq: "OBS Seq",
  sac_type: "SAC Type",
  coverage: "Coverage",
  coverage_start_date: "Coverage Start Date",
  coverage_end_date: "Coverage End Date",
  claim_id: "Claim ID",
  payer_code: "Payer Code",
  hospital_payer: "Hospital Payer",
  referer: "Referer",
  refer_no: "Refer No",
  insure_no: "Insure No",
  claim_code: "Claim Code",
  requesting_unit: "Requesting Unit",
  performing_unit: "Performing Unit",
  service_id: "Service ID",
  bill_mode_code: "Bill Mode Code",
  price_per_unit: "Price Per Unit",
  amount: "Amount",
  qty: "Quantity",
  unit_measurement: "Unit Measurement",
  discount_code: "Discount Code",
  discount_amount: "Discount Amount",
  additional_amount: "Additional Amount",
  doctor_code: "Doctor Code",
  reference_doc_no: "Reference Doc No",
  reference_doc_type: "Reference Doc Type",
  performed: "Performed",
  bank_transfer: "Bank Transfer",
  field_39: "Field 39",
  field_40: "Field 40",
  field_41: "Field 41",
  field_42: "Field 42",
  field_43: "Field 43",
  perform_date: "Perform Date",
  perform_time: "Perform Time",
  claimset_station: "Claimset Station",
  sale_return: "Sale Return",
  transferred: "Transferred",
  payment_status: "Payment Status",
  serial: "Serial",
  project_id: "Project ID",
  item_type: "Item Type",
  order_date: "Order Date",
  app_date: "App Date",
  receipt_date: "Recept Date",
  approve_code: "Approve Code",
  bil_station: "Bill Station",
  bil_date: "Bill Date",
  bil_time: "Bill time",
  invoice_item: "Invoice Item",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<BillingBLInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof BillingBLInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};